import { gql } from '@apollo/client/core'
import { Apollo } from './apollo'
import AppCheck from './appCheck'
import AppCheckRecovery from './appCheckRecovery'

export const AppCheckRecoveryResult = {
  Recovered: 'recovered',
  Rejected: 'rejected',
  ServerError: 'server_error',
  Unavailable: 'unavailable',
} as const

type AppCheckRecoveryResultValue = typeof AppCheckRecoveryResult[keyof typeof AppCheckRecoveryResult]

interface ApolloRecoveryError {
  graphQLErrors?: unknown[]
  networkError?: unknown
  statusCode?: number
}

const APP_CHECK_PROBE = gql`
  query appCheckProbe {
    usStates {
      __typename
    }
  }
`

let recoveryPromise: Promise<AppCheckRecoveryResultValue> | null = null

const performRecovery = async (): Promise<AppCheckRecoveryResultValue> => {
  const appCheckToken = await AppCheck.refreshAppCheckToken()

  if (!appCheckToken) {
    return AppCheckRecoveryResult.Unavailable
  }

  try {
    await Apollo.getClient().query({
      query: APP_CHECK_PROBE,
      context: { endpoint: 'backend', appCheckTokenOverride: appCheckToken },
      fetchPolicy: 'no-cache',
      errorPolicy: 'none',
    })
    return AppCheckRecoveryResult.Recovered
  } catch (error) {
    const recoveryError = error as ApolloRecoveryError | null
    const networkError = recoveryError?.networkError
    const rejected =
      AppCheckRecovery.isAppCheckRejection(error) ||
      AppCheckRecovery.isAppCheckRejection(networkError)

    if (rejected) {
      return AppCheckRecoveryResult.Rejected
    }

    const statusCode =
      recoveryError?.statusCode ?? (networkError as ApolloRecoveryError | null)?.statusCode
    const receivedServerResponse =
      statusCode !== undefined || Boolean(recoveryError?.graphQLErrors?.length)

    return receivedServerResponse
      ? AppCheckRecoveryResult.ServerError
      : AppCheckRecoveryResult.Unavailable
  }
}

const recover = (): Promise<AppCheckRecoveryResultValue> => {
  if (!recoveryPromise) {
    recoveryPromise = performRecovery().finally(() => {
      recoveryPromise = null
    })
  }

  return recoveryPromise
}

export default { recover }
